import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
    useWatch,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import {
    BusinessInput,
    PourTypeInput,
    PriceTierInput,
    ProductInput,
} from '@barboxx/admin-components';
import {
    CREATE_BUSINESS_PRODUCT_PRICE_TIER_SCHEMA,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessProductPriceTier } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateBusinessProductPriceTierDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notifications = useNotifications();
    const { businessId } = useBusinessSelector();

    const formContext = useForm<BusinessProductPriceTier.CreateRequest>({
        resolver: yupResolver(CREATE_BUSINESS_PRODUCT_PRICE_TIER_SCHEMA),
        defaultValues: {
            isActive: true,
            businessId,
            isDefault: false,
        },
    });

    const mutateCreateBusinessProductPriceTier = useMutation({
        mutationKey: ['businessProductPriceTier'],
        mutationFn: (data: BusinessProductPriceTier.CreateRequest) =>
            Api.BusinessSpecific.ProductPriceTier.postProductPriceTier(data),
    });

    const onSubmit = async (values: BusinessProductPriceTier.CreateRequest) => {
        return mutateCreateBusinessProductPriceTier
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created business product price tier!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                onSuccess();
            })
            .catch(e => {
                notifications.show(
                    'Error creating business product price tier!',
                    {
                        severity: 'error',
                    }
                );
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    const values = useWatch(
        formContext
    ) as BusinessProductPriceTier.CreateRequest;

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Business Product Price Tier
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <BusinessInput />
                    <ProductInput />
                    <PriceTierInput />
                    <PourTypeInput
                        onChange={() => {
                            formContext.setValue('isDefault', false);
                        }}
                    />

                    {values.pourTypeId && (
                        <TextFieldElement
                            label="Price Adjustment"
                            name="priceAdjustment"
                            type="number"
                            fullWidth
                            slotProps={{
                                htmlInput: {
                                    max: 1000,
                                    min: 0,
                                    step: 1,
                                },
                            }}
                        />
                    )}
                    <TextFieldElement
                        label="Display Order"
                        name="displayOrder"
                        type="number"
                        fullWidth
                        slotProps={{
                            htmlInput: {
                                max: 1000,
                                min: 0,
                                step: 1,
                            },
                        }}
                    />
                    <CheckboxElement label="Active" name="isActive" />
                    {!values.pourTypeId && (
                        <CheckboxElement label="Default" name="isDefault" />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
